import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { btn, btnAccent, bytes, when } from '../../lib/n4';

export interface GameBuild { id: number; project_id: number; title: string; note: string; entry: string; threads: boolean; files: number; bytes: number; by: string; created_at: number; url: string }

// Játék teszt: az ügynök által közzétett, böngészőben futó Godot Web (WebGL) buildek. A legfrissebb magától betölt,
// a korábbiak a listából választhatók (összehasonlításhoz); teljes képernyő, új lap, újratöltés, törlés.
export function GamePanel({ builds, onChange }: { builds: GameBuild[]; onChange: () => void }) {
  const [sel, setSel] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0);
  const [running, setRunning] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);
  const current = builds.find((b) => b.id === sel) ?? builds[0] ?? null;
  // Ha a kiválasztott build törlődött, a legújabbra állunk. A játék sosem indul magától (hangot ad és viszi a gépet).
  useEffect(() => { if (builds[0] && sel !== null && !builds.some((b) => b.id === sel)) setSel(builds[0].id); }, [builds, sel]);
  useEffect(() => { setRunning(false); }, [current?.id]);
  const remove = async (b: GameBuild) => { if (!confirm(`Törlöd ezt a buildet? „${b.title}”`)) return; await api(`/api/n4/games/${b.id}`, { method: 'DELETE' }); setSel(null); onChange(); };
  const fullscreen = () => { void frame.current?.requestFullscreen?.(); frame.current?.focus(); };
  if (!current) return (
    <div className="p-4 text-sm text-neutral-500">
      <p>Még nincs közzétett játék-build.</p>
      <p className="mt-2 text-[12px] text-neutral-600">Ez a fül böngészőben futó Godot (Web / WebGL) játékokhoz való: az ügynök exportálja a web-buildet, és az n4_game_publish eszközzel ide teszi. Kérd meg rá: „tedd ki a Játék tesztre”.</p>
    </div>
  );
  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--color-term-border)] px-3 py-2">
        <div className="min-w-0 flex-1 basis-full sm:basis-0">
          <div className="truncate text-sm font-semibold text-neutral-100">{current.title} <span className="ml-1 text-[11px] font-normal text-neutral-500">{when(current.created_at)} · {bytes(current.bytes)}{current.threads ? ' · szálas build' : ''}</span></div>
          {current.note && <div className="text-[12px] text-neutral-400">{current.note}</div>}
        </div>
        {builds.length > 1 && (
          <select className="min-w-0 max-w-full rounded-lg border border-[var(--color-term-border)] bg-black/40 px-2 py-1.5 text-xs text-neutral-200" value={current.id} onChange={(e) => setSel(Number(e.target.value))}>
            {builds.map((b, i) => <option key={b.id} value={b.id}>{i === 0 ? 'legújabb · ' : ''}{b.title} ({when(b.created_at)})</option>)}
          </select>
        )}
        {running && <button className={btn} onClick={() => setNonce((n) => n + 1)}>Újraindítás</button>}
        {running && <button className={btn} onClick={fullscreen}>Teljes képernyő</button>}
        <a className={`${btn} hub-btn`} href={current.url} target="_blank" rel="noreferrer">Új lapon</a>
        <button className={`${btn} hover:border-red-500/50 hover:text-red-400`} onClick={() => void remove(current)}>Törlés</button>
      </div>
      <div className="relative min-h-0 flex-1 bg-black">
        {current.threads ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-sm text-neutral-400">
            <p>Ez szálas (threads) build: beágyazva nem fut, új lapon igen.</p>
            <a className={`${btnAccent} hub-btn`} href={current.url} target="_blank" rel="noreferrer">Megnyitás új lapon</a>
          </div>
        ) : running ? (
          <iframe ref={frame} key={`${current.id}-${nonce}`} title={current.title} src={current.url} className="h-full w-full border-0" allow="fullscreen; gamepad; autoplay; clipboard-write" allowFullScreen />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <button className={`${btnAccent} px-6 py-3 text-base`} onClick={() => setRunning(true)}>▶ Játék indítása</button>
            <p className="text-[12px] text-neutral-600">A játék a böngésződben fut. Kattints bele, hogy megkapja az egeret és a billentyűzetet; Esc engedi el.</p>
          </div>
        )}
      </div>
    </div>
  );
}
